import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  AdvisorQuickPicker,
  type IAdvisor,
  type IAdvisorStarter,
} from "@/components/ai-assistant/advisor";
import { AnimatedAIAssistantIcon } from "@/components/ai-assistant/animated-ai-assistant-icon";
import { selectUser } from "@/store/slices/user.slice";
import { cn } from "@/utils/cn";
import { ArrowUpRight } from "lucide-react";
import { useSelector } from "react-redux";

interface AssistantHeroProps {
  advisor: IAdvisor;
  onSelectAdvisor: (slug: string) => void;
  onSelectPrompt: (prompt: string) => void;
  /** Advisor-specific suggestion cards — each scope keeps its own copy
   *  (company-wide questions vs personal ones), so it's injected rather than
   *  imported here. */
  starters: IAdvisorStarter[];
  /** Resolves the advisor row copy (My Data Only vs Company Only, etc.) for
   *  the employee-facing surface. */
  isPersonal?: boolean;
  isTempChat?: boolean;
}

/** Plain helper — takes the translator rather than calling a hook. */
const salutation = (t: TFunction) => {
  const hour = new Date().getHours();
  if (hour < 12) return t("common:ai_assistant.good_morning");
  if (hour < 17) return t("common:ai_assistant.good_afternoon");
  return t("common:ai_assistant.good_evening");
};

/**
 * The assistant's landing state. Three decisions drive the layout:
 *
 * 1. The advisor row sits above the suggestions, because it *changes* them —
 *    picking Payroll & Tax and still being offered an unrelated suggestion
 *    teaches the user the picker does nothing.
 * 2. Suggestions carry a prompt that answers in one shot, so the first
 *    interaction is a real answer rather than a clarifying question.
 * 3. Entrance is staggered top-down so the eye lands on the headline first;
 *    the whole stagger is dropped under `prefers-reduced-motion`.
 */
export const AssistantHero = ({
  advisor,
  onSelectAdvisor,
  onSelectPrompt,
  starters,
  isPersonal = false,
  isTempChat,
}: AssistantHeroProps) => {
  const { t } = useTranslation(["common"]);
  const { user } = useSelector(selectUser);

  return (
    <div className="flex w-full min-w-0 select-none flex-col items-center gap-4 sm:gap-5">
      <div
        className="animate-rise-in flex flex-col items-center gap-2 sm:gap-3"
        style={{ animationDelay: "40ms" }}
      >
        <AnimatedAIAssistantIcon />

        <div className="flex flex-col items-center gap-1 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {isTempChat ? (
              t("common:ai_assistant.temporary_chat_nothing_stored")
            ) : (
              <>
                {salutation(t)}
                {user?.firstName ? `, ${user.firstName}` : ""}
              </>
            )}
          </p>
          <h2 className="moving-gradient-bg bg-clip-text! text-xl font-semibold text-transparent sm:text-2xl md:text-4xl">
            {t("common:ai_assistant.what_can_i_look_into")}
          </h2>
          {/* Only the temp-chat caveat earns a line here. A generic "ask about
              leave, payroll…" subtitle repeats what the cards below show. */}
          {isTempChat && (
            <p className="max-w-[46ch] text-sm text-muted-foreground">
              {t("common:ai_assistant.session_not_saved")}
            </p>
          )}
        </div>
      </div>

      {/* Full width so the chip row has something to scroll inside on phones. */}
      <div
        className="animate-rise-in w-full min-w-0"
        style={{ animationDelay: "120ms" }}
      >
        <AdvisorQuickPicker
          advisor={advisor}
          onSelect={onSelectAdvisor}
          isPersonal={isPersonal}
        />
      </div>

      <div
        className="animate-rise-in grid w-full min-w-0 max-w-2xl grid-cols-1 gap-3 sm:grid-cols-2"
        style={{ animationDelay: "200ms" }}
      >
        {starters.map(({ icon: Icon, title, description, prompt }) => (
          <button
            key={title}
            type="button"
            onClick={() => onSelectPrompt(prompt)}
            className={cn(
              "surface-frosted group relative overflow-hidden rounded-lg border p-3 text-start sm:p-3.5",
              "flex items-start gap-2.5 transition-all duration-200 sm:gap-3",
              "hover:-translate-y-0.5 hover:border-primary hover:shadow-md",
              "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
            )}
          >
            <span className="bg-primary-muted text-primary group-hover:bg-primary flex size-8 shrink-0 items-center justify-center rounded-md transition-colors duration-200 group-hover:text-white sm:size-9">
              <Icon className="size-4" />
            </span>

            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-foreground">
                {title}
              </span>
              <span className="block text-xs text-muted-foreground">
                {description}
              </span>
            </span>

            <ArrowUpRight className="text-muted-foreground hidden size-4 shrink-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100 sm:block" />
          </button>
        ))}
      </div>
    </div>
  );
};
